import _ from 'lodash';
// @mui
import { Stack } from '@mui/system';
import { Box, Chip, Typography } from '@mui/material';
//components
import FormCreator from 'components/FormCreator';
import { fieldsGroupType, fieldType, invalidFields, option, validationTypes } from 'components/FormCreator/types';
//reducers
import { selectTranslations } from 'state/reducers/translation.reducers';
import { getNewConvert } from 'state/reducers/convert.reducers';
import { useAppSelector } from 'hooks/redux.hooks';
import { getCurrencies, getCurrencyPairs } from 'state/reducers/global.reducers';
//sections
import { FormCard } from 'theme/elements/Forms';
import { FormHeader1 } from 'theme/elements/Headers';
//constants
import { balanceitem, Convert, convertType } from 'constants/convert.constants';
import { Fragment, useMemo } from 'react';
import { getCompanyBalances } from 'state/reducers/balance.reducers';
import { formatNumber } from 'components/Currency';

export default function ConvertDetailsForm(props: { handleChange: any; invalidFields: invalidFields }) {
	const t = useAppSelector(selectTranslations);
	const convert: Convert = useAppSelector(getNewConvert);
	const currencyPairs = useAppSelector(getCurrencyPairs);
	const prefixFields = 'page.convert.create.step.1.convert_details.';
	let Currencies = useAppSelector(getCurrencies);
	let companyBalancesList: balanceitem[] = useAppSelector(getCompanyBalances);

	const allowedCurrencies = useMemo(() => {
		let arr: option[] = [];
		let objCorrencies = _.groupBy(Currencies, 'id');
		companyBalancesList.forEach((item: balanceitem) => {
			if (_.has(objCorrencies, item.currencyId) && item.amount! > 0) {
				let obj = {
					...objCorrencies[item?.currencyId][0],
					amount: item.amount,
				};
				arr = [...arr, obj];
			}
		});
		return arr;
	}, [companyBalancesList]);

	const currentCurrency = useMemo(() => {
		return _.find(allowedCurrencies, { code: convert?.sell_currency }) || {};
	}, [convert.sell_currency]);

	const isBuyCurrenciesDisabled = useMemo(
		() => _.isNil(convert.sell_currency) || !currencyPairs[convert.sell_currency!],
		[currencyPairs, convert]
	);

	const invalidFields = useMemo(() => {
		if (isBuyCurrenciesDisabled) {
			const { amount, buy_currency, ...data } = props.invalidFields;

			return data;
		}
		return props.invalidFields;
	}, [props.invalidFields, isBuyCurrenciesDisabled]);

	const buyCurrencies = useMemo(() => {
		return !isBuyCurrenciesDisabled
			? _.filter(Currencies, (currency) => currencyPairs[convert.sell_currency!].includes(currency.code))
			: [];
	}, [convert.sell_currency]);

	const amountPlaceholder = useMemo(() => {
		const currency =
			convert?.type === convertType.sell
				? _.find(allowedCurrencies, { code: convert?.sell_currency }) || {}
				: _.find(buyCurrencies, { code: convert?.buy_currency }) || {};
		return (_.get(currency, 'symbol') || '') + '0.00';
	}, [allowedCurrencies, convert.buy_currency, convert.sell_currency, convert.type, buyCurrencies]);

	return (
		<Box component="form" noValidate>
			<FormCard>
				<FormHeader1 title={t(`${prefixFields}title`)} text={t(`${prefixFields}text`)} />
				<Stack spacing={2}>
					<FormCreator
						initialValidation={invalidFields}
						name="localPayment"
						handleChange={props.handleChange}
						fieldsGroups={[
							{
								type: fieldsGroupType.col,
								fields: [
									{
										type: fieldType.radio,
										props: {
											required: true,
											name: 'type',
											label: t(`${prefixFields}convert_type.text`),
											value: convert.type,
											options: [
												{
													value: convertType.buy,
													label: t(
														`${prefixFields}convert_type.${convertType.buy}_radio.text`
													),
												},
												{
													value: convertType.sell,
													label: t(
														`${prefixFields}convert_type.${convertType.sell}_radio.text`
													),
												},
											],
										},
									},
									{
										type: fieldType.select,
										valueProperty: 'code',
										labelProperty: 'customName',
										renderOption: (props, option) => {
											return convert?.buy_currency === option.code ? (
												<Fragment key={option.code}></Fragment>
											) : (
												<Box
													component="li"
													disabled={true}
													{...props}
													className=""
													sx={{
														padding: '8px 16px',
														display: 'flex',
														justifyContent: 'space-between',
													}}
												>
													<Typography>{option.label}</Typography>
													<Chip
														size="small"
														label={option.symbol + option.amount?.toLocaleString()}
													/>
												</Box>
											);
										},
										props: {
											sx: {
												display: 'inline-block',
												'& .MuiInputBase-root': {
													'&:before': {
														position: 'absolute',
														display: !_.isEmpty(currentCurrency) ? 'block' : 'none',
														content:
															'"' +
															_.get(currentCurrency, 'symbol') +
															_.get(currentCurrency, 'amount', '')?.toLocaleString() +
															'"',
														height: '24px',
														lineHeight: '24px',
														borderRadius: '16px',
														backgroundColor: 'rgba(145, 158, 171, 0.16)',
														padding: '0 8px',
														right: '60px',
													},
												},
											},
											required: true,
											name: 'sell_currency',
											options: allowedCurrencies,
											label: t(`${prefixFields}sell_currency.text`),
											value: convert.sell_currency || '',
										},
									},
									{
										type: _.isNil(convert.sell_currency) ? fieldType.text : fieldType.select,
										validation: [validationTypes.requierd],
										valueProperty: 'code',
										labelProperty: 'customName',
										props: {
											disabled: isBuyCurrenciesDisabled,
											required: true,
											name: 'buy_currency',
											options: _.values(buyCurrencies),
											label: t(`${prefixFields}buy_currency.text`),
											value: convert.buy_currency || '',
										},
										getOptionDisabled: (option) => {
											return convert?.sell_currency === option.code ? true : false;
										},
										renderOption: (props, option) => {
											let flag = true;
											let code = _.get(currentCurrency, 'code');
											if (_.has(currencyPairs, code || '')) {
												const pairs = _.get(currencyPairs, code || '');
												flag = _.includes(pairs, option.value);
											}
											return !flag ? (
												false
											) : (
												<Box
													key={option.code}
													component="li"
													{...props}
													sx={{ padding: '8px 16px' }}
												>
													<Typography>{option.label}</Typography>
												</Box>
											);
										},
									},
									{
										type: fieldType.text,
										validation: [validationTypes.requierd],
										props: {
											formatAsAmount: true,
											required: true,
											name: 'amount',
											label: t(`${prefixFields}${convert.type}_amount.text`),
											placeholder: amountPlaceholder,
											value: convert.amount,
											helperText:
												_.keys(currentCurrency).length
													? t(`${prefixFields}available_${convert.type}_amount.text`) + ' ' +
													  _.get(currentCurrency, 'symbol') +
													  _.get(currentCurrency, 'amount', '').toLocaleString()
													: '',
										},
									},
								],
							},
						]}
					/>
				</Stack>
			</FormCard>
		</Box>
	);
}
